import {
  type Availability,
  type AvailabilityUpdate,
  type Home,
  type User,
  UserTransportation
} from '@lmc/ts-common'
import {
  Button,
  EmptyState,
  Form,
  FormCard,
  FormItem,
  FormRadio,
  FormRadioGroup,
  NoDataIcon,
  PageHeader,
  Select,
  TextArea,
  Trash,
  theme
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import QuestionMarkIcon from '@lmc/ui-kit/src/general/MoreInfoLink/QuestionMarkIcon'
import {
  Form as AntdForm,
  DatePicker,
  InputNumber,
  TimePicker,
  Tooltip
} from 'antd'
import type { DefaultOptionType } from 'antd/lib/select'
import { showToast } from 'common/utils'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import dayjs from 'dayjs'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useHomeType from 'hooks/useHomeType'
import { useRouter } from 'hooks/useRouter'
import useUser from 'hooks/useUser/useUser'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { InputSuffix } from 'screens/Rostering/formComponents'
import { handleOnFinishFailed } from 'screens/Rostering/formComponents/utils'
import { styled } from 'styled-components'
import { ButtonsContainer, PageWrapper } from '../../components'
import {
  OverrideLevel,
  type PaymentCard,
  useGetPaymentCards
} from '../../hooks/usePaymentCards'
import { useShiftTypesList } from '../../hooks/useShiftTypesList'
import {
  useCreateUserPayrate,
  useDeleteUserPayrate,
  useGetUserSettings,
  useUpdateUserPayrate
} from '../../hooks/useUserSettings'
import { useVisitTypesList } from '../../hooks/useVisitTypesList'
import { formatRateDisplay } from '../../siteSettings/components/PayRateCard'
import { getRequiredRule } from '../../utils'
import {
  DATE_FORMAT,
  disabledPastDate,
  getDisabledPastTime,
  TIME_FORMAT
} from '../../utils.datetime'
import { defaultAvailability } from '../utils'
import AvailabilityPreferenceTable from './components/AvailabilityPreferenceTable'

const payRateOptionValue = (card: PaymentCard) =>
  card.previousVersion?.initialId ?? card.initialId

const payRateCardSelectLabel = (card: PaymentCard) =>
  card.previousVersion?.name ?? card.name

const effectivePayRateCard = (card: PaymentCard | undefined) =>
  card?.previousVersion ?? card

type ContractAndPreferencesForm = {
  contractType?: string
  contractedHoursPerWeek?: number
  contractNotes?: string
  canDoOvertime?: boolean
  availableTransportation?: UserTransportation[]
  availability?: Availability
  rotaPreferenceNotes?: string
}

export const EditContractAndPreferencesScreen = () => {
  const { t } = useTranslation()
  const [form] = AntdForm.useForm()
  const topRef = useRef<HTMLDivElement | null>(null)
  const { navigate, params } = useRouter()
  const { user, updateUser, isLoadingUpdate } = useUser({
    id: params?.id,
    includes: ['rostering'],
    onUpdateSuccess: () => navigate(-1)
  })
  const [isDirty, setIsDirty] = useState(false)
  const [payrateTouched, setPayrateTouched] = useState(false)
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )
  const { hasRota } = useHomeType()

  const flags = useFeatureFlags({ flagNames: ['payrates'] })?.data
  const isPayRatesEnabled = flags?.payrates?.enabled

  const { data: paymentCards } = useGetPaymentCards(!!isPayRatesEnabled)
  const { shiftTypes } = useShiftTypesList()
  const { visitTypes } = useVisitTypesList()
  const { data: userSettings } = useGetUserSettings(
    isPayRatesEnabled ? params?.id : undefined
  )
  const { createPayrate } = useCreateUserPayrate(params?.id)
  const { updatePayrate } = useUpdateUserPayrate(params?.id)
  const { deletePayrate } = useDeleteUserPayrate(params?.id)

  const eventTypeNameMap = useMemo<Record<string, string>>(() => {
    const allEventTypes = [
      ...(shiftTypes || [])
        .filter((st) => st.id)
        .map((st) => ({ id: st.id!, name: st.name })),
      ...(visitTypes || [])
        .filter((vt) => vt.id)
        .map((vt) => ({ id: vt.id!, name: vt.name }))
    ]
    return Object.fromEntries(allEventTypes.map((et) => [et.id, et.name]))
  }, [shiftTypes, visitTypes])

  const previousVersion = userSettings?.previousVersion
  const payRateOptions: DefaultOptionType[] =
    paymentCards
      ?.filter((card) =>
        previousVersion ? true : card.initialId !== userSettings?.payRateCardId
      )
      .filter((card) => Boolean(payRateOptionValue(card)))
      .map((card) => ({
        label: payRateCardSelectLabel(card),
        value: payRateOptionValue(card) as string
      })) || []

  const selectedPayRateCardInitialId = AntdForm.useWatch(
    'payRateCardInitialId',
    form
  )
  const selectedPayRateCard = paymentCards?.find(
    (card) => payRateOptionValue(card) === selectedPayRateCardInitialId
  )
  const displayPayRateCard = useMemo(
    () => effectivePayRateCard(selectedPayRateCard),
    [selectedPayRateCard]
  )

  const hasCurrentPayrate = !!userSettings?.payRateCardId
  const currentPayRateCard =
    previousVersion?.payRateCard ?? userSettings?.payRateCard

  useEffect(() => {
    if (!previousVersion || !userSettings?.payRateCardId) return
    const card = paymentCards?.find(
      (c) => c.initialId === userSettings.payRateCardId
    )
    if (!card) return
    form.setFieldsValue({
      payRateCardInitialId: payRateOptionValue(card),
      ...(userSettings.effectiveFrom
        ? {
            payRateEffectiveDate: dayjs(userSettings.effectiveFrom),
            payRateEffectiveTime: dayjs(userSettings.effectiveFrom)
          }
        : {})
    })
  }, [previousVersion, userSettings, paymentCards, form])

  const contractTypeOptions: DefaultOptionType[] =
    home.rotaSettings?.contractTypes?.map((contractType) => ({
      label: contractType.name,
      // @ts-ignore not from my MR
      value: contractType.id
    })) || []

  const updateAvailability = (
    currentAvailability: Availability,
    updates: AvailabilityUpdate
  ): Availability => {
    // @ts-ignore
    const updatedAvailability: AvailabilityUpdate = {}

    Object.keys(currentAvailability).forEach((day) => {
      // @ts-ignore
      updatedAvailability[day] = {}
      // @ts-ignore
      currentAvailability[day].forEach((timeslot, idx) => {
        // @ts-ignore
        updatedAvailability[day][timeslot.name] = updates[day][idx].available
      })
    })

    // @ts-ignore
    return updatedAvailability
  }

  const handleOnUpdateFinish = async (values: any) => {
    setIsDirty(false)

    const {
      payRateCardInitialId,
      payRateEffectiveDate,
      payRateEffectiveTime,
      ...workProfileValues
    } = values

    if (isPayRatesEnabled && payrateTouched) {
      const isCreate = !hasCurrentPayrate && !!payRateCardInitialId
      const isUpdate = hasCurrentPayrate && !!payRateCardInitialId
      const isDelete = hasCurrentPayrate && !payRateCardInitialId

      if (isCreate) {
        await createPayrate({ payRateCardInitialId })
        showToast({ type: 'success', title: t('PAY_RATE_CREATED') })
      } else if (isUpdate) {
        const effectiveFrom = dayjs(payRateEffectiveDate)
          .set('hour', dayjs(payRateEffectiveTime).hour())
          .set('minute', dayjs(payRateEffectiveTime).minute())
          .set('second', 0)
          .set('millisecond', 0)
          .toISOString()

        await updatePayrate({ payRateCardInitialId, effectiveFrom })
        showToast({ type: 'success', title: t('PAY_RATE_UPDATED') })
      } else if (isDelete) {
        await deletePayrate()
        showToast({ type: 'success', title: t('PAY_RATE_DELETED') })
      }
    }

    updateUser({
      workProfile: {
        ...workProfileValues,
        ...(hasRota && {
          availability: updateAvailability(
            initialValues.availability || defaultAvailability,
            // @ts-ignore
            workProfileValues.availability
          ) // only until BE is fixed
        })
      }
    } as User)
  }

  const payrateFields = new Set([
    'payRateCardInitialId',
    'payRateEffectiveDate',
    'payRateEffectiveTime'
  ])

  const handleFormValuesChange = (changedValues: any) => {
    if (changedValues) {
      setIsDirty(true)
      if (Object.keys(changedValues).some((key) => payrateFields.has(key))) {
        setPayrateTouched(true)
      }
    }
  }
  const userWithWorkProfile = user as unknown as User

  const initialValues: ContractAndPreferencesForm = {
    availableTransportation: [],
    ...userWithWorkProfile?.workProfile
  }

  const transportationOptions = Object.entries(UserTransportation).map(
    ([key, value]) => ({
      key,
      value
    })
  )

  return (
    <div ref={topRef} data-testid="edit-contract-and-preferences-screen">
      <PageHeader
        title={`${t('EDIT')} ${
          hasRota ? t('CONTRACT_AND_PREFERENCES') : t('CONTRACT')
        }`}
        sticky
        data-testid="edit-contract-and-preferences-header"
      >
        <ButtonsContainer data-testid="edit-contract-and-preferences-actions">
          <Button
            onClick={() => navigate(-1)}
            data-cy="cancel-contract-btn"
            data-testid="edit-contract-and-preferences-cancel-button"
          >
            {t('CANCEL')}
          </Button>
          <Button
            onClick={form.submit}
            variant="primary"
            isLoading={isLoadingUpdate}
            data-cy="save-contract-btn"
            data-testid="edit-contract-and-preferences-save-button"
          >
            {t('SAVE')}
          </Button>
        </ButtonsContainer>
      </PageHeader>
      <PageWrapper data-testid="edit-contract-and-preferences-content">
        <Form<ContractAndPreferencesForm>
          form={form}
          onFinish={handleOnUpdateFinish}
          onValuesChange={handleFormValuesChange}
          initialValues={initialValues}
          onFinishFailed={({ errorFields }) =>
            handleOnFinishFailed(errorFields)
          }
          data-cy="edit-mode-contract-container"
          data-testid="edit-contract-and-preferences-form"
        >
          <FormCard
            title={t('CONTRACT_INFORMATION')}
            data-testid="edit-contract-and-preferences-contract-card"
          >
            <FormItem
              name="contractType"
              label={<Text>{t('CONTRACT_TYPE')}</Text>}
              rules={[getRequiredRule(t('CONTRACT_TYPE'))]}
              data-testid="edit-contract-and-preferences-contract-type-field"
            >
              <Select
                options={contractTypeOptions}
                data-testid="edit-contract-and-preferences-contract-type-select"
                notFoundContent={
                  <EmptyState
                    title={
                      <EmptyStateWrapper>
                        <Text>{t('SELECT_EMPTY_STATE_CONTRACT_TYPES')}</Text>
                      </EmptyStateWrapper>
                    }
                    description={
                      <Link
                        to="/settings/contracts-and-absences"
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Button variant="primary">{t('ADD')}</Button>
                      </Link>
                    }
                    icon={
                      <NoDataIconWrapper>
                        <NoDataIcon />
                      </NoDataIconWrapper>
                    }
                  />
                }
              />
            </FormItem>
            <FormItem
              name="contractedHoursPerWeek"
              label={<Text>{t('CONTRACTED_HOURS_PER_WEEK')}</Text>}
              rules={[getRequiredRule(t('CONTRACTED_HOURS_PER_WEEK'))]}
              data-testid="edit-contract-and-preferences-contracted-hours-field"
            >
              <InputNumber
                type="number"
                controls={false}
                suffix={<InputSuffix>{t('HOURS_PER_WEEK')}</InputSuffix>}
                style={{ width: '100%' }}
                min={0}
                max={168}
                data-testid="edit-contract-and-preferences-contracted-hours-input"
              />
            </FormItem>
            <StyledFormItem
              name="contractNotes"
              label={<Text>{t('CONTRACT_NOTES')}</Text>}
              data-testid="edit-contract-and-preferences-contract-notes-field"
            >
              <TextArea
                autoSize={{
                  minRows: 3
                }}
                maxLength={200}
                showCount
                data-testid="edit-contract-and-preferences-contract-notes-textarea"
              />
            </StyledFormItem>
          </FormCard>
          {hasRota && (
            <FormCard
              title={t('OTHER')}
              data-testid="edit-contract-and-preferences-other-card"
            >
              {isPayRatesEnabled && (
                <>
                  {hasCurrentPayrate && currentPayRateCard && (
                    <CurrentPayRateBox>
                      <CurrentPayRateLabel>
                        <Text> {t('CURRENT_PAY_RATE')}</Text>
                      </CurrentPayRateLabel>
                      <CurrentPayRateContent>
                        <CurrentPayRateName>
                          {currentPayRateCard.name}
                        </CurrentPayRateName>
                        {currentPayRateCard.rates.map((rate) => {
                          const label =
                            rate.level === OverrideLevel.BASE
                              ? t('BASE_RATE')
                              : eventTypeNameMap[rate.eventTypeId!] ||
                                rate.eventTypeId
                          return (
                            <span key={rate.eventTypeId ?? 'base'}>
                              {label}:{' '}
                              {formatRateDisplay(rate.rate, rate.rateType, t)}
                              <br />
                            </span>
                          )
                        })}
                        {previousVersion?.effectiveFrom && (
                          <span>
                            {t('EFFECTIVE_DATE')}:{' '}
                            {dayjs(previousVersion.effectiveFrom).format(
                              DATE_FORMAT
                            )}{' '}
                            {dayjs(previousVersion.effectiveFrom).format(
                              TIME_FORMAT
                            )}
                          </span>
                        )}
                      </CurrentPayRateContent>
                    </CurrentPayRateBox>
                  )}
                  <SelectRow>
                    <FormItem
                      name="payRateCardInitialId"
                      label={
                        <StyledLabel>
                          <Text>
                            {hasCurrentPayrate
                              ? t('UPDATED_PAY_RATE')
                              : t('PAY_RATE')}
                          </Text>
                          {!hasCurrentPayrate && (
                            <Tooltip
                              title={
                                <TooltipText>
                                  {t('PAY_RATE_POPOVER_PREFIX')}{' '}
                                  <TooltipLink to="/settings/pay-rates">
                                    {t('PAY_RATE_POPOVER_LINK')}
                                  </TooltipLink>{' '}
                                  {t('PAY_RATE_POPOVER_SUFFIX')}
                                </TooltipText>
                              }
                              overlayInnerStyle={{
                                backgroundColor: '#000',
                                borderRadius: '9px',
                                padding: '8px 12px'
                              }}
                              color="#000"
                            >
                              <QuestionMarkContainer>
                                <QuestionMarkIcon />
                              </QuestionMarkContainer>
                            </Tooltip>
                          )}
                        </StyledLabel>
                      }
                      style={{ flex: 1, marginBottom: 0 }}
                    >
                      <Select options={payRateOptions} allowClear />
                    </FormItem>
                    {hasCurrentPayrate && selectedPayRateCardInitialId && (
                      <Button
                        variant="text"
                        type="button"
                        icon={<Trash color={theme.color.neutral.color4} />}
                        onClick={() => {
                          form.setFieldsValue({
                            payRateCardInitialId: undefined,
                            payRateEffectiveDate: undefined,
                            payRateEffectiveTime: undefined
                          })
                          setPayrateTouched(true)
                        }}
                      >
                        <DeleteText>{t('DELETE_UPDATE')}</DeleteText>
                      </Button>
                    )}
                  </SelectRow>
                  {displayPayRateCard && (
                    <RatePreviewCard>
                      {displayPayRateCard.rates.map((rate) => {
                        const label =
                          rate.level === OverrideLevel.BASE
                            ? t('BASE_RATE')
                            : eventTypeNameMap[rate.eventTypeId!] ||
                              rate.eventTypeId
                        return (
                          <RatePreviewItem key={rate.eventTypeId ?? 'base'}>
                            <RatePreviewLabel>{label}</RatePreviewLabel>
                            <RatePreviewValue>
                              {formatRateDisplay(rate.rate, rate.rateType, t)}
                            </RatePreviewValue>
                          </RatePreviewItem>
                        )
                      })}
                    </RatePreviewCard>
                  )}
                  {hasCurrentPayrate && selectedPayRateCardInitialId && (
                    <>
                      <FormItem
                        name="payRateEffectiveDate"
                        label={<Text>{t('EFFECTIVE_DATE')}</Text>}
                        rules={[getRequiredRule(t('EFFECTIVE_DATE'))]}
                      >
                        <DatePicker
                          format={DATE_FORMAT}
                          disabledDate={disabledPastDate}
                        />
                      </FormItem>
                      <FormItem
                        name="payRateEffectiveTime"
                        label={<Text>{t('EFFECTIVE_TIME')}</Text>}
                        rules={[getRequiredRule(t('EFFECTIVE_TIME'))]}
                      >
                        <TimePicker
                          format={TIME_FORMAT}
                          needConfirm={false}
                          disabledTime={() =>
                            getDisabledPastTime(
                              form.getFieldValue('payRateEffectiveDate')
                            )
                          }
                        />
                      </FormItem>
                    </>
                  )}
                </>
              )}
              <FormItem
                name="canDoOvertime"
                label={<Text>{t('OVERTIME')}</Text>}
                rules={[getRequiredRule(t('OVERTIME'))]}
                data-testid="edit-contract-and-preferences-overtime-field"
              >
                <FormRadioGroup data-testid="edit-contract-and-preferences-overtime-radio-group">
                  <FormRadio
                    value
                    text={t('WILLING_TO_WORK_OVERTIME')}
                    data-testid="edit-contract-and-preferences-overtime-yes"
                  />
                  <FormRadio
                    value={false}
                    text={t('PREFERS_NOT_TO_WORK_OVERTIME')}
                    data-testid="edit-contract-and-preferences-overtime-no"
                  />
                </FormRadioGroup>
              </FormItem>
              <FormItem
                name="availableTransportation"
                label={<Text>{t('TYPE_OF_TRANSPORT')}</Text>}
                data-testid="edit-contract-and-preferences-transportation-field"
              >
                <Select
                  mode="tags"
                  options={transportationOptions}
                  data-testid="edit-contract-and-preferences-transportation-select"
                />
              </FormItem>
              <StyledFormItem
                name="rotaPreferenceNotes"
                label={
                  <StyledLabel>
                    <Text>{t('ROTA_PREFERENCES')}</Text>
                    <Tooltip title={t('ROTA_PREFERENCES_DESC_TOOLTIP')}>
                      <QuestionMarkContainer>
                        <QuestionMarkIcon />
                      </QuestionMarkContainer>
                    </Tooltip>
                  </StyledLabel>
                }
                data-testid="edit-contract-and-preferences-rota-preferences-field"
              >
                <TextArea
                  maxLength={200}
                  autoSize={{
                    minRows: 3
                  }}
                  showCount
                  data-testid="edit-contract-and-preferences-rota-preferences-textarea"
                />
              </StyledFormItem>
              {/* <FormItem
                  name="rotaTags"
                  label={
                    <>
                      <Text>{t('ROTA_TAGS')}</Text>
                      <MoreInfoLink helpArticleUrl="todo" />
                    </>
                  }
                >
                  <Select mode="tags" />
                </FormItem> */}
            </FormCard>
          )}
          {hasRota && (
            <FormCard
              title={t('AVAILABILITY_PREFERENCE')}
              subtitle={t('AVAILABILITY_PREFERENCE_DESC')}
              data-testid="edit-contract-and-preferences-availability-card"
            >
              <AvailabilityPreferenceTable
                editMode
                availability={
                  userWithWorkProfile?.workProfile?.availability ||
                  defaultAvailability
                }
                testId="edit-contract-and-preferences-availability"
              />
            </FormCard>
          )}
        </Form>
      </PageWrapper>
      <NavigationModal
        canNavigate={!isDirty}
        modalContentText={t('CONTRACT_PREFERENCES_NAVIGATION_MODAL_CONTENT')}
      />
    </div>
  )
}

const QuestionMarkContainer = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
`

const StyledLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`

const StyledFormItem = styled(FormItem)`
  padding-bottom: 24px;
`
const EmptyStateWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const NoDataIconWrapper = styled.div`
  scale: 0.5;
  height: 50px;
  display: flex;
  align-items: center;
`

const RatePreviewCard = styled.div`
  background: ${({ theme }) => theme.color.neutral.color10};
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 380px;
  margin-left: 200px;
`

const RatePreviewItem = styled.div``

const RatePreviewLabel = styled(Text).attrs({ level: 2 })`
  color: ${({ theme }) => theme.color.neutral.color5};
  font-weight: 500;
  display: block;
`

const RatePreviewValue = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
  display: block;
`

const TooltipText = styled(Text)`
  color: #fff;
  font-weight: 400;
`

const CurrentPayRateBox = styled.div`
  display: flex;
  border: 1px solid ${({ theme }) => theme.color.neutral.color8};
  border-radius: 8px;
  overflow: hidden;
`

const CurrentPayRateLabel = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  background: ${({ theme }) => theme.color.neutral.color10};
  color: ${({ theme }) => theme.color.neutral.color5};
  min-width: 100px;
  font-size: 13px;
`

const CurrentPayRateContent = styled.div`
  padding: 16px;
  font-size: 14px;
  line-height: 1.6;
`

const CurrentPayRateName = styled.div`
  font-weight: 600;
`

const SelectRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
`

const DeleteText = styled.span`
  color: ${({ theme }) => theme.color.neutral.color4};
  font-weight: 500;
`

const TooltipLink = styled(Link)`
  color: #69b1ff;
  text-decoration: underline;

  &:hover {
    color: #91caff;
  }
`
